import { Link as RouterLink } from "react-router-dom";
import { Link, Button } from "@mui/material";
import { ChargerActionMenu } from "./ChargerActionMenu";
import {
  f,
  yearFirstDateTimeFormat,
} from "src/utils/formatTime";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ThrottledComponent from "src/common/components/ThrottledComponent";
import { Icon } from "@iconify/react";
import eyeFill from "@iconify/icons-eva/eye-fill";

export const chargerPointColumns =(isReadOnly)=>( [
  {
    field: "chargepoint_id",
    headerName: "Chargebox ID",
    flex: 0.8,
    minWidth:110,
    resizable: true,
    renderCell: ({ value, id }) => (<>
      { isReadOnly?value:<Link underline="none" component={RouterLink} to={`${id}`}>
        {value}
      </Link>}
    </>
    ),
  },
  {
    field: "chargepoint_name",
    headerName: "Charger Name",
    flex: 0.6,
    minWidth:130,
    resizable: true,
  },
  {
    field: "team_name",
    headerName: "Team",
    flex: 0.6,
    minWidth:130,
    resizable: true,
  },
  {
    field: "street",
    headerName: "Address",
    flex: 1,
    minWidth:250,
    resizable: true,
  },
  {
    field: "connectors",
    headerName: "Connectors",
    flex: 0.8,
    minWidth: 160,
    renderCell: ({ value }) => {
      if (!value || value.length === 0) return "N/A";
  
      const renderConnector = (connector, index) => {
        const isAvailable =
          connector.status?.toLowerCase() === "available" ||
          connector.status?.toLowerCase() === "preparing";
  
        const icon = isAvailable ? "mdi:check" : "mdi:close";
        const iconColor = isAvailable ? "#4caf50" : "#FF0000";
  
        return (
          <div
            key={index}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: "13px",
              lineHeight: "1.4",
            }}
          >
            <Icon icon={icon} color={iconColor} width={16} height={16} />
            <div>
              <div style={{ fontWeight: 500 }}>
                Connector: {connector.connectorName ?? "N/A"}
              </div>
              <div
                style={{
                  textTransform: "capitalize",
                  fontSize: "12px",
                  color: "#333",
                }}
              >
                {connector.status ?? "N/A"}
              </div>
            </div>
          </div>
        );
      };
  
      const hasMultiple = value.length > 1;
  
      return hasMultiple ? (
        <Tooltip
          placement="right"
          arrow
          disableInteractive
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: "#fff",
                color: "#000",
                boxShadow: 3,
                borderRadius: 1,
                overflowY: "auto",
                padding: 1,
              },
            },
          }}
          title={
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {value.map(renderConnector)}
            </div>
          }
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              cursor: "default",
              userSelect: "none",
            }}
          >
            {renderConnector(value[0])}
            <span
              style={{
                fontSize: "11px",
                color: "#999",
                marginLeft: 22,
              }}
            >
              +{value.length - 1} more... hover to view
            </span>
          </div>
        </Tooltip>
      ) : (
        renderConnector(value[0])
      );
    },
  },
  {
    field: "description",
    headerName: "Description",
    flex: 1,
    minWidth:120,
    resizable: true,
  },
  {
    field: "visibility",
    headerName: "Visibility",
    flex: 0.6,
    minWidth:90,
    resizable: true,
    renderCell: ({ value }) => (
      <span>{Number(value) === 0 ? "Private" : "Public"}</span>
    ),
  },
  {
    field: "status",
    headerName: "Status",
    flex: 0.6,
    minWidth:90,
    renderCell: ({ value }) => {
      let statusColor = ["inactive", "lt", "na"].includes(value.toLowerCase()) ? "red" : "green";
      return (
        <span style={{ marginLeft: "10px", color: statusColor }}>
          {["a", "active"].includes(value.toLowerCase())
            ? "Active"
            : "Inactive"}
        </span>
      );
    },
  },
  {
    field: "lastHeartbeatTimestamp",
    headerName: "Last Connected",
    type: "date",
    flex: 1,
    minWidth:180,
    renderCell: ({ value }) => <span>{yearFirstDateTimeFormat(value)}</span>,
  },
  {
    field: "Actions",
    width: 80,
    sortable: false,
    renderCell: (props) => (
      <ThrottledComponent>
        <ChargerActionMenu {...props} />
      </ThrottledComponent>
    ),
  },
]);

export const ChargerFormTabs = [
  {value:"general",label:"GENERAL"},
  {value:"address",label:"ADDRESS"},
  {value:"license",label:"LICENSE DETAILS"},
  {value:"settings",label:"SETTINGS"},
  {value:"remote operations",label:"REMOTE OPERATIONS"},
  { value: "operating_instruction", label:"OPERATING INSTRUCTIONS" },
  { value: "report_incidents", label: "REPORTED INCIDENTS" },
  // {value:"config",label:"CONFIG"},
  {value:"logs",label:"LOGS"},
]
export const hideForTabs = ["remote operations","logs","config","license"]
export const getChargerDefaultValues = ({ teamId }) => ({
  chargepoint_id: "",
  chargepoint_name:"",
  excludeFromEmail: false,
  registrationStatus: "",
  charger_type:"DC",
  description: "",
  adminAddress: "",
  locationLongitude: "",
  locationLatitude: "",
  note: "",
  endpointAddress: "",
  ocppProtocol: "",
  chargePointVendor: "",
  chargePointSerialNumber: "",
  chargeBoxSerialNumber: "",
  fwVersion: "",
  fwUpdateStatus: "",
  fwUpdateTimestamp: "",
  iccid: "",
  imsi: "",
  meterType: "",
  meterSerialNumber: "",
  diagnosticsStatus: "",
  diagnosticsTimestamp: "",
  lastHeartbeatTimestamp: "",
  insertConnectorStatusAfterTransactionMsg: false,
  name: "",
  street: "",
  houseNumber: "",
  zipCode: "",
  city: "",
  country: "",
  state: "",
  priceGroupId:null,
  companyId: teamId,
  connectors: [
    { connectorId: "", kw: "", volt: "", connectorType: "", priceGroupId: "" },
  ],
  showOnPortal: false,
  employeeOrResident: true,
  payWithWallet: true,
  payWithCard: true,
  enableNotifications:true,
  notificationRecipients:"",
  OcppStationBooted:true,
  OcppStationStatusFailure:true,
  OcppStationWebSocketConnected:true,
  OcppStationWebSocketDisconnected:true,
  OcppTransactionStarted:true,
  OcppTransactionEnded:true,
  tolerance_watt:300,
  meter_interval_sec:30,
  amenities: [],
  photos: [],
  operator_email: "",
  operator_number1: "",
  operator_number2: "",
  faq: [
    { question: "", answer: "", id: 0 }
  ]
});

export const NOTIFICATION_CONSTANTS = [
  "OcppStationBooted",
  "OcppStationStatusFailure",
  "OcppStationWebSocketConnected",
  "OcppStationWebSocketDisconnected",
  "OcppTransactionStarted",
  "OcppTransactionEnded",
]

export const AMENITIES = [
 { text: "Gym", value: "GYM" },
 { text: "Cinema", value: "THEATER" },
 { text: "Salon", value: "SALON" },
 { text: "Pilgrimage Sites", value: "PILGRIMAGE SITE" },
 { text: "Public Washroom", value: "PUBLIC WASHROOM" },
 { text: "Park", value: "PARK" },
 { text: "Kid Acitivity Zone", value: "KID ACTIVITY ZONE" },
 { text: "Auto Service Center", value: "AUTO SERVICE" },
 { text: "Medical Store", value: "MEDICAL STORE" },
 { text: "Hospital", value: "HOSPITAL" },
 { text: "24/7 Monitoring", value: "24/7 MONITORING" },
 { text: "Hotel", value: "HOTEL" },
 { text: "ATM", value: "ATM" },
 { text: "Restaurant", value: "RESTAURANT" },
 { text: "Shopping Center", value: "SHOPPING" },
 { text: "Parking", value: "PARKING" },
];

export const reportIssuesColumn = [
  {
    field: "serial_number",
    headerName: "Serial",
    flex: 0.8,
  },
  {
    field: "charge_box_id",
    headerName: "Charger ID",
    flex: 1,
  },
  {
    field: "created_at",
    headerName: "Date & Time",
    type: "date",
    flex: 1,
    renderCell: ({ value }) => <span>{yearFirstDateTimeFormat(value)}</span>,
  },
  {
    field: "category",
    headerName: "Reported Issues Category",
    flex: 1,
    renderCell: ({ value }) => (
      <Tooltip title={value} placement="top">
        <Typography noWrap>{value}</Typography>
      </Tooltip>
    )
  },
  {
    field: "comment",
    headerName: "Comments",
    flex: 1,
    renderCell: ({ value }) => (
      <Tooltip title={value} placement="top">
        <Typography noWrap>{value}</Typography>
      </Tooltip>
    )
  }
]

export const AllStatus = [
  { value: "Available", label: "Available" },
  { value: "Preparing", label: "Preparing" },
  { value: "Charging", label: "Charging" },
  { value: "SuspendedEV", label: "SuspendedEV" },
  { value: "SuspendedEVSE", label: "SuspendedEVSE" },
  { value: "Finishing", label: "Finishing" },
  { value: "Faulted", label: "Faulted" },
  { value: "Reserved", label: "Reserved" },
  { value: "Unavailable", label: "Unavailable" },
]

export const AllTypes = [
  { value: "Authorize", label: "Authorize" },
  { value: "AuthorizeResponse", label: "AuthorizeResponse" },
  { value: "BootNotification", label: "BootNotification" },
  { value: "BootNotificationResponse", label: "BootNotificationResponse" },
  { value: "Heartbeat", label: "Heartbeat" },
  { value: "HeartbeatResponse", label: "HeartbeatResponse" },
  { value: "StatusNotification", label: "StatusNotification" },
  { value: "StartTransaction", label: "StartTransaction" },
  { value: "StartTransactionResponse", label: "StartTransactionResponse" },
  { value: "StopTransaction", label: "StopTransaction" },
  { value: "StopTransactionResponse", label: "StopTransactionResponse" },
  { value: "MeterValues", label: "MeterValues" },
  { value: "RemoteStartTransaction", label: "RemoteStartTransaction" },
  { value: "RemoteStopTransaction", label: "RemoteStopTransaction" },
  { value: "DiagnosticsStatusNotification", label: "DiagnosticsStatusNotification" },
]

export const AllMessageStatus = [
  { value: "Success", label: "Success" },
  { value: "Error", label: "Error" },
]

export const Direction = [
  { value: "Incoming", label: "Incoming"},
  { value: "Outgoing", label: "Outgoing" },
]

export const tabsToShow = [
  { value: "expiredLicenses", label: "Expired Licenses" },
  { value: "upcomingLicensesExpiry", label: "Upcoming Licenses Expiry" },
]

export const licenseColumns = (handleViewChargepoint) =>  [
    {
      field: "chargebox_id",
      headerName: "Charger ID",
      flex: 1,
      minWidth: 120,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "expiry",
      headerName: "Expiry Date",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      renderCell: ({ value }) => (
        <span>{value ? f(value, "dd/MM/yyyy") : '-'}</span>
      ),
    },
    {
      field: "final_expiry",
      headerName: "Final Expiry",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      renderCell: ({ value }) => (
        <span>{value ? f(value, "dd/MM/yyyy") : '-'}</span>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          startIcon={<Icon icon={eyeFill} />}
          onClick={() => handleViewChargepoint(params.row.charge_box_pk)}
          sx={{
            backgroundColor: "#00AB55",
            color: "white",
            "&:hover": {
              backgroundColor: "#007B55",
            },
          }}
        >
          View
        </Button>
      ),
    },
  ];